/**
 * Cartões de ambiente das listas do mapa: as bandejas ("Ainda no escritório"/"Sem sala no mapa",
 * "Obra futura") e os resultados da busca. Cada cartão diz onde o ambiente
 * está, o prazo com a régua dele e o que está pendurado nele (pendências
 * paralelas da planilha, peças desmembradas, movido à mão).
 */
import React from "react";

import { formatarDataLocal, plural } from "../../utils/dinheiro";
import { SITUACAO, localDaDemanda } from "./setores";
import { Bloco, Cartao, Contador, Selo, Selos, TituloBloco } from "./styles";

function textoTempo(demanda) {
  if (demanda.diasNoSetor == null) return null;
  const onde = demanda.situacao === SITUACAO.SETOR ? "no setor" : "na etapa";
  return `${onde} há ${plural(demanda.diasNoSetor, "dia", "dias")}`;
}

// `prazo` já é Date local (toDate da planilha); o helper da casa formata.
function textoPrazo(demanda) {
  if (!demanda.prazo) return "Sem prazo";
  const dia = formatarDataLocal(demanda.prazo);
  return demanda.atrasada ? `Atrasada · prazo ${dia}` : `Prazo ${dia}`;
}

function textoMarceneiro(marceneiro) {
  if (!marceneiro || marceneiro.semMarceneiro) return null;
  return marceneiro.tipo ? `${marceneiro.nome} (${marceneiro.tipo})` : marceneiro.nome;
}

/** Selos do cartão: estado, o que trava e o que saiu do ambiente. */
export function selosDaDemanda(demanda) {
  const selos = [];
  if (demanda.emProducao) selos.push({ id: "em_producao", tom: "info", texto: "Em produção" });
  if (demanda.pecasFora > 0) {
    selos.push({
      id: "pecas_fora",
      tom: "neutral",
      texto: `${plural(demanda.pecasFora, "peça", "peças")} fora`,
    });
  }
  // Alocação que contraria a planilha: o gestor precisa saber por que aquele
  // ambiente não está onde a etapa diria.
  if (demanda.movidoAMao) {
    selos.push({
      id: "movido",
      tom: "neutral",
      texto: demanda.setorNomePlanilha ? `Movido à mão (planilha: ${demanda.setorNomePlanilha})` : "Movido à mão",
    });
  }
  // Toda pendência paralela quer dizer a mesma coisa — algo de fora ainda não
  // chegou —, então todas têm o mesmo tom. Cor comunica estado, não categoria.
  (demanda.pendencias || []).forEach((p) => selos.push({ id: p.codigo, tom: "warning", texto: p.texto }));
  return selos;
}

export function CartaoDemanda({ demanda, mostrarLocal = false, onAbrir }) {
  const titulo = [demanda.pedido, demanda.ambiente].filter(Boolean).join(" · ") || "Sem identificação";
  const marceneiro = textoMarceneiro(demanda.marceneiro);
  const tempo = textoTempo(demanda);
  const selos = selosDaDemanda(demanda);
  return (
    <Cartao type="button" onClick={() => onAbrir(demanda)} title={titulo}>
      <span className="titulo">{titulo}</span>
      <span className="linha">
        {demanda.cliente && <span>{demanda.cliente}</span>}
        {demanda.lote && <span>Lote {demanda.lote}</span>}
        {marceneiro && <span>Marceneiro {marceneiro}</span>}
        {mostrarLocal && <span>{localDaDemanda(demanda)}</span>}
      </span>
      <span className="linha">
        {tempo && <span>{tempo}</span>}
        <span className={demanda.atrasada ? "prazo-atrasado" : undefined}>
          {textoPrazo(demanda)}
        </span>
      </span>
      {selos.length > 0 && (
        <Selos>
          {selos.map((selo) => <Selo key={selo.id} $tom={selo.tom}>{selo.texto}</Selo>)}
        </Selos>
      )}
    </Cartao>
  );
}

/** Blocos `{chave, titulo, tom, itens}` com os cartões. */
export default function ListaDemandas({ blocos, onAbrir, mostrarLocal = false, vazio, acaoDoBloco = null }) {
  if (!blocos.length) return <p>{vazio}</p>;
  return blocos.map((bloco) => (
    <Bloco key={bloco.chave} aria-label={bloco.titulo}>
      <TituloBloco>
        {bloco.titulo}
        <Contador $tom={bloco.tom}>{bloco.itens.length}</Contador>
        {acaoDoBloco?.(bloco)}
      </TituloBloco>
      {bloco.itens.map((demanda) => (
        <CartaoDemanda
          key={demanda.chaveLinha || demanda.chave}
          demanda={demanda}
          mostrarLocal={mostrarLocal}
          onAbrir={onAbrir}
        />
      ))}
    </Bloco>
  ));
}
